import { useCallback, type MutableRefObject } from 'react';
import { MENTION_TAG_NAME, VARIABLE_TAG_NAME } from '../constants';
import { duplicateTagAtPosition } from '../tiptapEditorUtils';
import { useMentionMenuKeyboard } from './useMentionMenuKeyboard';
import type { Editor } from '@tiptap/react';
import type { AvailableInput, PromptEditorContextMenuState } from '../types';
import type { PromptDisplayMode } from 'web';

interface UseTiptapEditorKeyboardParams {
  editorRef: MutableRefObject<Editor | null>;
  availableInputs: AvailableInput[];
  displayMode: PromptDisplayMode;
  contextMenu: PromptEditorContextMenuState;
  handleSetContextMenu: (newState: PromptEditorContextMenuState) => void;
  handleMentionMenuDetection: (event: KeyboardEvent) => boolean;
  onCreatePrompt: (queryText: string) => { label: string; key: string } | undefined;
}

export const useTiptapEditorKeyboard = ({
  editorRef,
  availableInputs,
  displayMode,
  contextMenu,
  handleSetContextMenu,
  handleMentionMenuDetection,
  onCreatePrompt,
}: UseTiptapEditorKeyboardParams) => {
  const { handleMentionMenuKeyboard } = useMentionMenuKeyboard({
    editor: editorRef.current,
    contextMenu,
    handleSetContextMenu,
    displayMode,
    onCreatePrompt,
  });
  /**
   * Handles Ctrl/Cmd+C to stop propagation and prevent Flow.jsx from copying node JSON.
   */
  const handleDOMKeyboardCopy = useCallback((event: KeyboardEvent): void => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'c') {
      event.stopPropagation();
    }
  }, []);

  /**
   * Handles Cmd/Ctrl+D for duplicating the selected tag.
   * Only duplicates if the selection is exactly one inline tag.
   */
  const handleDOMKeyboardDuplicate = useCallback(
    (event: KeyboardEvent): boolean => {
      if (!editorRef.current) return false;

      if ((event.metaKey || event.ctrlKey) && event.key === 'd') {
        event.preventDefault();
        event.stopPropagation();

        const { state } = editorRef.current;
        const { selection, doc } = state;
        const { from, to } = selection;

        // Check if selection is exactly one inlineTag (no text)
        const resolvedFrom = doc.resolve(from);
        const node = resolvedFrom.nodeAfter;

        // Check if it's an inlineTag and selection spans exactly this node
        if (node && node.type.name === VARIABLE_TAG_NAME && to === from + node.nodeSize) {
          const tagKey = node.attrs.tagKey as string;

          // Don't duplicate mention trigger
          if (tagKey === MENTION_TAG_NAME) return false;

          // Duplicate the tag
          duplicateTagAtPosition({
            editor: editorRef.current,
            tagKey,
            tagPos: from,
            availableInputs,
            displayMode,
          });

          return true;
        }
      }

      return false;
    },
    [editorRef, availableInputs, displayMode],
  );

  const handleEditorDOMKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Stop Ctrl+C/Cmd+C propagation to prevent Flow.jsx from copying node JSON
      handleDOMKeyboardCopy(event);

      // Handle Cmd/Ctrl+D for duplicating selected tag
      return handleDOMKeyboardDuplicate(event);
    },
    [handleDOMKeyboardCopy, handleDOMKeyboardDuplicate],
  );

  const handleEditorKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!editorRef.current) return false;

      // Handle mention menu detection
      const mentionDetected = handleMentionMenuDetection(event);

      if (mentionDetected) return true;

      // Handle mention menu keyboard events when editor has focus
      return handleMentionMenuKeyboard(event);
    },
    [editorRef, handleMentionMenuDetection, handleMentionMenuKeyboard],
  );

  return {
    handleEditorKeyDown,
    handleEditorDOMKeyDown,
  };
};
